import { z } from 'zod';

export const eventContextSchema = z.object({
  sdk_version: z.string(),
  device: z.string(),
  os: z.string(),
  app_version: z.string(),
});
export type EventContext = z.infer<typeof eventContextSchema>;

// name format guard: catches typos and cardinality explosions from stuffing
// dynamic data into the name (e.g. "clicked_button_" + id) — not a closed
// vocabulary, customers define their own event names.
export const eventNameSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z][a-z0-9_]*$/, 'name must be snake_case, e.g. "purchase_completed"');

// properties stay free-form (event shape varies per name, unknown at
// ingestion time — same reasoning as every analytics SDK) but bounded so a
// pathological payload can't bloat storage: capped key count and value size,
// no nested objects/arrays.
export const propertyValueSchema = z.union([
  z.string().max(1000),
  z.number(),
  z.boolean(),
  z.null(),
]);
const propertiesSchema = z
  .record(z.string(), propertyValueSchema)
  .refine((props) => Object.keys(props).length <= 50, {
    message: 'properties must have at most 50 keys',
  });

export const eventSchema = z.object({
  schema_version: z.literal('v1'),
  event_id: z.string().uuid(), // client-generated, enables idempotent ingestion/dedupe
  org_id: z.string(),
  app_id: z.string(),
  env: z.string(),
  anonymous_id: z.string(),
  user_id: z.string().optional(),
  name: eventNameSchema,
  properties: propertiesSchema,
  context: eventContextSchema,
  timestamp: z.string().datetime(), // client-set
  received_at: z.string().datetime(), // server-set
});
export type Event = z.infer<typeof eventSchema>;
